import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Search, BookOpen, ChevronDown, ChevronRight, Package, Wrench,
  Calculator, Shield, ArrowRightLeft, FileText, Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SpecSection({ spec, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const products = spec.products || [];
  const rules = spec.calculation_rules || [];
  const install = spec.installation_requirements || [];
  const standards = spec.quality_standards || [];
  const asmComps = spec.assembly_components || [];
  const subs = spec.substitutions || [];

  return (
    <div className="border border-[#E2E8ED] rounded-lg overflow-hidden" data-testid={`spec-section-${spec.spec_section}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-[#F7FAFB] transition-colors"
      >
        {open ? <ChevronDown className="w-4 h-4 text-[#72A4BF] shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#72A4BF] shrink-0" />}
        <Badge className="text-[10px] px-2 py-0.5 rounded bg-[#0D1830] text-white border-0 font-mono shrink-0">
          {spec.spec_section}
        </Badge>
        <span className="text-sm font-medium text-[#0D1830] flex-1 truncate">{spec.spec_title}</span>
        <div className="flex items-center gap-2 shrink-0">
          {products.length > 0 && <Badge className="text-[9px] px-1.5 py-0 rounded-full bg-blue-50 text-blue-600 border-0">{products.length} product{products.length > 1 ? 's' : ''}</Badge>}
          {rules.length > 0 && <Badge className="text-[9px] px-1.5 py-0 rounded-full bg-emerald-50 text-emerald-600 border-0">{rules.length} rule{rules.length > 1 ? 's' : ''}</Badge>}
          {asmComps.length > 0 && <Badge className="text-[9px] px-1.5 py-0 rounded-full bg-indigo-50 text-indigo-600 border-0">{asmComps.length} component{asmComps.length > 1 ? 's' : ''}</Badge>}
        </div>
      </button>

      {open && (
        <div className="border-t border-[#E2E8ED] p-4 space-y-4 bg-white">
          {/* Products */}
          {products.length > 0 && (
            <div>
              <h4 className="text-[10px] font-semibold text-[#72A4BF] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Package className="w-3 h-3" /> Products
              </h4>
              <div className="space-y-2">
                {products.map((p, i) => (
                  <div key={i} className="flex items-start gap-3 p-2 rounded-md bg-blue-50/40">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[#0D1830]">{p.name}</p>
                      <div className="flex items-center gap-3 mt-0.5">
                        {p.manufacturer && <span className="text-[10px] text-[#446E87]">{p.manufacturer}</span>}
                        {p.model && <span className="text-[10px] text-[#72A4BF]">{p.model}</span>}
                      </div>
                      {p.properties && <p className="text-[10px] text-[#72A4BF] mt-0.5">{p.properties}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Calculation Rules */}
          {rules.length > 0 && (
            <div>
              <h4 className="text-[10px] font-semibold text-[#72A4BF] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Calculator className="w-3 h-3" /> Calculation Rules
              </h4>
              <ul className="space-y-1">
                {rules.map((r, i) => (
                  <li key={i} className="text-xs text-[#0D1830] flex items-start gap-2 p-1.5 rounded-md bg-emerald-50/40">
                    <span className="text-emerald-500 mt-0.5 shrink-0">&#8226;</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Assembly Components */}
          {asmComps.length > 0 && (
            <div>
              <h4 className="text-[10px] font-semibold text-[#72A4BF] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Wrench className="w-3 h-3" /> Assembly Components
              </h4>
              <div className="grid grid-cols-2 gap-1.5">
                {asmComps.map((c, i) => (
                  <div key={i} className="text-xs p-2 rounded-md bg-indigo-50/40 flex items-center justify-between">
                    <span className="text-[#0D1830]">{c.component}</span>
                    <span className="text-[10px] text-indigo-600 font-mono">{c.quantity_per_unit} {c.unit}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Installation */}
          {install.length > 0 && (
            <div>
              <h4 className="text-[10px] font-semibold text-[#72A4BF] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Wrench className="w-3 h-3" /> Installation Requirements
              </h4>
              <ul className="space-y-1">
                {install.map((r, i) => (
                  <li key={i} className="text-xs text-[#446E87] flex items-start gap-2">
                    <span className="text-[#72A4BF] mt-0.5 shrink-0">&#8226;</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Quality Standards */}
          {standards.length > 0 && (
            <div>
              <h4 className="text-[10px] font-semibold text-[#72A4BF] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Shield className="w-3 h-3" /> Quality Standards
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {standards.map((s, i) => (
                  <Badge key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border-0">{s}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Substitutions */}
          {subs.length > 0 && (
            <div>
              <h4 className="text-[10px] font-semibold text-[#72A4BF] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <ArrowRightLeft className="w-3 h-3" /> Substitutions
              </h4>
              <ul className="space-y-1">
                {subs.map((s, i) => (
                  <li key={i} className="text-xs text-[#446E87]">{s}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Source */}
          {(spec.source_file || spec.page_range) && (
            <div className="pt-2 border-t border-[#E2E8ED] flex items-center gap-3 text-[10px] text-[#72A4BF]">
              <FileText className="w-3 h-3" />
              {spec.source_file && <span>{spec.source_file}</span>}
              {spec.page_range && <span>{spec.page_range}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SpecViewer({ projectId }) {
  const [specData, setSpecData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchSpecs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/spec-data`, { credentials: 'include' });
      if (res.ok) setSpecData(await res.json());
    } catch (e) {}
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchSpecs(); }, [fetchSpecs]);

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-[#1D4053] animate-spin" /></div>;

  const specs = specData?.specifications || [];
  const generalReqs = specData?.general_requirements || [];

  const filtered = specs.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (s.spec_section || '').toLowerCase().includes(q) ||
      (s.spec_title || '').toLowerCase().includes(q) ||
      (s.products || []).some(p => (p.name || '').toLowerCase().includes(q) || (p.manufacturer || '').toLowerCase().includes(q)) ||
      (s.calculation_rules || []).some(r => r.toLowerCase().includes(q))
    );
  });

  if (specs.length === 0) {
    return (
      <div className="text-center py-12" data-testid="spec-viewer-empty">
        <BookOpen className="w-8 h-8 text-[#72A4BF]/40 mx-auto mb-3" />
        <p className="text-sm text-[#72A4BF]">No spec data available</p>
        <p className="text-[10px] text-[#72A4BF]/60 mt-1">Upload a spec sheet in the Documents tab to see extracted specifications here</p>
      </div>
    );
  }

  // Summary counts
  const totalProducts = specs.reduce((sum, s) => sum + (s.products?.length || 0), 0);
  const totalRules = specs.reduce((sum, s) => sum + (s.calculation_rules?.length || 0), 0);
  const totalComponents = specs.reduce((sum, s) => sum + (s.assembly_components?.length || 0), 0);

  return (
    <div className="space-y-4" data-testid="spec-viewer">
      {/* Summary */}
      <div className="flex items-center gap-4">
        <Badge className="text-[10px] px-2.5 py-1 rounded-full bg-[#0D1830] text-white border-0">
          {specs.length} Section{specs.length !== 1 ? 's' : ''}
        </Badge>
        <Badge className="text-[10px] px-2.5 py-1 rounded-full bg-blue-50 text-blue-600 border-0">
          {totalProducts} Product{totalProducts !== 1 ? 's' : ''}
        </Badge>
        <Badge className="text-[10px] px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600 border-0">
          {totalRules} Calculation Rule{totalRules !== 1 ? 's' : ''}
        </Badge>
        {totalComponents > 0 && (
          <Badge className="text-[10px] px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-600 border-0">
            {totalComponents} Component{totalComponents !== 1 ? 's' : ''}
          </Badge>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#72A4BF]" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search specs by section, product, or keyword..."
          className="pl-10 h-9 rounded-lg text-sm"
          data-testid="spec-search"
        />
      </div>

      {/* Sections */}
      <div className="space-y-2">
        {filtered.map((spec, i) => (
          <SpecSection key={`${spec.spec_section}-${i}`} spec={spec} defaultOpen={filtered.length <= 3} />
        ))}
        {filtered.length === 0 && search && (
          <p className="text-center text-sm text-[#72A4BF] py-6">No specifications match "{search}"</p>
        )}
      </div>

      {/* General Requirements */}
      {generalReqs.length > 0 && (
        <Card className="border-[#E2E8ED] shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold text-[#72A4BF] uppercase tracking-wider">General Requirements</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="space-y-1">
              {generalReqs.map((r, i) => (
                <li key={i} className="text-xs text-[#446E87] flex items-start gap-2">
                  <span className="text-[#72A4BF] mt-0.5">&#8226;</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Source info */}
      {specData?.source_files?.length > 0 && (
        <p className="text-[10px] text-[#72A4BF] flex items-center gap-1.5">
          <FileText className="w-3 h-3" />
          Source: {specData.source_files.join(', ')} — Processed {specData.processed_at ? new Date(specData.processed_at).toLocaleDateString() : ''}
        </p>
      )}
    </div>
  );
}
